import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { Crown, Pencil, Plus, Trash2, Users, Workflow } from "lucide-react"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import type { Team } from "@/api/generated/Team"
import { useDeleteTeam, useTeams } from "@/api/queries/teams"
import { useConfirm } from "@/components/app/confirm-dialog"
import { createColumns, DataTable } from "@/components/app/data-table"
import { PageContainer, PageHeader } from "@/components/app/page"
import { RelativeTime } from "@/components/app/relative-time"
import { EmptyState } from "@/components/app/states"
import { StatusBadge } from "@/components/app/status-badge"
import { Button } from "@/components/ui/button"
import { DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { AgentAvatar } from "@/features/studio/agent-avatar"
import { AutomationsPanel } from "@/features/teams/automations-panel"
import { sortMembers } from "@/features/teams/roster"
import { TeamDialog } from "@/features/teams/team-dialog"
import { formatNumber } from "@/lib/format"

interface TeamsSearch {
  tab?: "automations"
}

export const Route = createFileRoute("/_app/studio/teams/")({
  staticData: { titleKey: "nav.items.teams" },
  validateSearch: (s: Record<string, unknown>): TeamsSearch => ({
    tab: s.tab === "automations" ? "automations" : undefined,
  }),
  component: TeamsPage,
})

function TeamsPage() {
  const { t } = useTranslation()
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const [dialog, setDialog] = useState<{ open: boolean; team?: Team }>({ open: false })
  const tab = search.tab ?? "teams"

  return (
    <PageContainer width="wide">
      <PageHeader
        title={t("studio.teams.title")}
        description={t("studio.teams.description")}
        icon={Users}
        actions={
          tab === "teams" && (
            <Button onClick={() => setDialog({ open: true })} data-testid="team-new">
              <Plus />
              {t("studio.teams.new")}
            </Button>
          )
        }
      >
        <Tabs
          value={tab}
          onValueChange={(v) =>
            void navigate({ search: { tab: v === "automations" ? "automations" : undefined }, replace: true })
          }
        >
          <TabsList variant="line" className="h-8">
            <TabsTrigger value="teams" className="px-2">
              <Users />
              {t("studio.teams.tabs.teams")}
            </TabsTrigger>
            <TabsTrigger value="automations" className="px-2">
              <Workflow />
              {t("studio.teams.tabs.automations")}
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </PageHeader>
      {tab === "teams" ? (
        <TeamsTable
          onCreate={() => setDialog({ open: true })}
          onEdit={(team) => setDialog({ open: true, team })}
        />
      ) : (
        <AutomationsPanel />
      )}
      <TeamDialog
        open={dialog.open}
        team={dialog.team}
        onOpenChange={(open) => setDialog((d) => ({ ...d, open }))}
        onCreated={(team) =>
          void navigate({ to: "/studio/teams/$teamId", params: { teamId: String(team.id) } })
        }
      />
    </PageContainer>
  )
}

function MemberStack({ team }: { team: Team }) {
  const { t } = useTranslation()
  const members = sortMembers(team.members)
  const shown = members.slice(0, 5)
  if (members.length === 0)
    return <span className="text-muted-foreground text-xs">{t("studio.teams.noMembers")}</span>
  return (
    <div className="flex items-center gap-2">
      <div className="flex -space-x-1.5" aria-hidden>
        {shown.map((m) => (
          <AgentAvatar key={m.id} name={m.name} size="sm" className="ring-card ring-2" />
        ))}
      </div>
      <span className="text-muted-foreground text-xs tabular-nums">
        {t("studio.teams.memberCount", { count: members.length })}
      </span>
    </div>
  )
}

function TeamsTable({ onCreate, onEdit }: { onCreate: () => void; onEdit: (team: Team) => void }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const teams = useTeams()
  const remove = useDeleteTeam()
  const confirm = useConfirm()
  const open = (team: Team) =>
    void navigate({ to: "/studio/teams/$teamId", params: { teamId: String(team.id) } })

  const columns = useMemo(
    () =>
      createColumns<Team>((c) => [
        c.accessor("name", {
          header: t("studio.teams.columns.team"),
          enableHiding: false,
          cell: ({ row }) => (
            <div className="max-w-md min-w-48">
              <div className="truncate font-medium">{row.original.name}</div>
              <div className="text-muted-foreground truncate text-xs">
                {row.original.description || t("studio.teams.noDescription")}
              </div>
            </div>
          ),
        }),
        c.display({
          id: "members",
          header: t("studio.teams.columns.members"),
          cell: ({ row }) => <MemberStack team={row.original} />,
        }),
        c.display({
          id: "leader",
          header: t("studio.teams.columns.leader"),
          cell: ({ row }) => {
            const leader = row.original.members.find((m) => m.is_leader)
            return leader ? (
              <span className="inline-flex max-w-40 items-center gap-1.5 text-sm">
                <Crown className="text-warning size-3.5 shrink-0" />
                <span className="truncate">{leader.name}</span>
              </span>
            ) : (
              <span className="text-muted-foreground text-xs">—</span>
            )
          },
        }),
        c.accessor("ready", {
          header: t("studio.teams.columns.status"),
          cell: ({ row }) =>
            row.original.ready ? (
              <StatusBadge tone="success" dot>
                {t("studio.teams.ready")}
              </StatusBadge>
            ) : (
              <StatusBadge tone="warning" dot title={row.original.ready_error ?? undefined}>
                {t("studio.teams.notReady")}
              </StatusBadge>
            ),
        }),
        c.accessor("room_count", {
          header: t("studio.teams.columns.rooms"),
          meta: { align: "end" },
          cell: ({ getValue }) => <span className="tabular-nums">{formatNumber(getValue())}</span>,
        }),
        c.accessor("updated_at", {
          header: t("studio.teams.columns.updated"),
          sortFn: "datetime",
          cell: ({ getValue }) => <RelativeTime value={getValue()} className="text-muted-foreground" />,
        }),
      ]),
    [t],
  )

  return (
    <DataTable
      label={t("studio.teams.title")}
      data={teams.data}
      columns={columns}
      getRowId={(team) => String(team.id)}
      loading={teams.isPending}
      error={teams.error}
      onRetry={() => void teams.refetch()}
      searchable
      searchPlaceholder={t("studio.teams.search")}
      initialSorting={[{ id: "name", desc: false }]}
      onRowClick={open}
      rowActions={(team) => (
        <>
          <DropdownMenuItem onClick={() => open(team)}>
            <Users />
            {t("studio.teams.actions.open")}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onEdit(team)}>
            <Pencil />
            {t("common.edit")}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            variant="destructive"
            onClick={() =>
              void confirm({
                title: t("studio.teams.deleteTitle", { name: team.name }),
                description:
                  team.room_count > 0
                    ? t("studio.teams.deleteInUse", { count: team.room_count })
                    : t("studio.teams.deleteDescription"),
                confirmLabel: t("studio.teams.delete"),
                destructive: true,
                confirmText: team.name,
                onConfirm: () => remove.mutateAsync(team),
              })
            }
          >
            <Trash2 />
            {t("studio.teams.delete")}
          </DropdownMenuItem>
        </>
      )}
      emptyState={
        <EmptyState
          variant="plain"
          icon={Users}
          title={t("studio.teams.empty.title")}
          description={t("studio.teams.empty.description")}
          action={
            <Button size="sm" onClick={onCreate}>
              <Plus />
              {t("studio.teams.new")}
            </Button>
          }
        />
      }
    />
  )
}
